import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT } from '@iot-platform/core';
import { UserApiKeyHelpers } from '@iot-platform/iot4bos/util/users';
import { CommonApiListResponse, Environment } from '@iot-platform/models/common';
import { UserApiKey } from '@iot-platform/models/i4b';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class UserApiKeysService {
  private environment: Environment = inject(ENVIRONMENT);
  private httpClient: HttpClient = inject(HttpClient);

  getUserApiKeys(userId: string): Observable<UserApiKey[]> {
    return this.httpClient
      .get<
        CommonApiListResponse<UserApiKey>
      >(`${this.environment.api.url}${this.environment.api.endpoints.users}/${userId}${this.environment.api.endpoints.userApiKeys}`)
      .pipe(map((response: CommonApiListResponse<UserApiKey>) => response.content?.map((key) => UserApiKeyHelpers.withIsExpired(key))));
  }

  createUserApiKey(userId: string, userApiKey: UserApiKey): Observable<UserApiKey> {
    const { businessProfile, ...payload } = userApiKey;
    return this.httpClient
      .post<UserApiKey>(`${this.environment.api.url}${this.environment.api.endpoints.users}/${userId}${this.environment.api.endpoints.userApiKeys}`, {
        ...payload,
        businessProfileId: businessProfile.id
      })
      .pipe(map((key) => UserApiKeyHelpers.withIsExpired(key)));
  }

  updateUserApiKey(userApiKey: UserApiKey): Observable<UserApiKey> {
    return this.httpClient
      .patch<UserApiKey>(
        `${this.environment.api.url}${this.environment.api.endpoints.users}/${userApiKey.userId}${this.environment.api.endpoints.userApiKeys}/${userApiKey.id}`,
        {
          status: userApiKey.status,
          description: userApiKey.description
        }
      )
      .pipe(map((key) => UserApiKeyHelpers.withIsExpired(key)));
  }

  renewUserApiKey(userApiKey: UserApiKey): Observable<UserApiKey> {
    return this.httpClient
      .post<UserApiKey>(
        `${this.environment.api.url}${this.environment.api.endpoints.users}/${userApiKey.userId}${this.environment.api.endpoints.userApiKeys}/${userApiKey.id}/renew`,
        { expiresAt: userApiKey.expiresAt }
      )
      .pipe(map((key) => UserApiKeyHelpers.withIsExpired(key)));
  }

  updateUserApiKeyUsagePlan(userApiKey: UserApiKey): Observable<UserApiKey> {
    return this.httpClient
      .patch<UserApiKey>(
        `${this.environment.api.url}${this.environment.api.endpoints.users}/${userApiKey.userId}${this.environment.api.endpoints.userApiKeys}/${userApiKey.id}/usage-plan`,
        userApiKey.usagePlan
      )
      .pipe(map(() => userApiKey));
  }

  deleteUserApiKey(userApiKey: UserApiKey): Observable<UserApiKey> {
    return this.httpClient
      .delete<UserApiKey>(
        `${this.environment.api.url}${this.environment.api.endpoints.users}/${userApiKey.userId}${this.environment.api.endpoints.userApiKeys}/${userApiKey.id}`
      )
      .pipe(map(() => userApiKey));
  }
}
